import { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router';
import { Button, Stack } from '@mui/material';
import clsx from 'clsx';
import sitemap, { MenuItem } from 'routes/sitemap';
import { getCoupleSitemap, getGuestSitemap } from 'routes/wedding-sitemap';
import IconifyIcon from 'components/base/IconifyIcon';
import { useAuth } from 'providers/AuthProvider';
import { useNavContext } from '../NavProvider';
import NavitemPopover from './NavItemPopover';

interface TopnavItemsProps {
  type?: 'default' | 'slim';
}

const TopnavItems = ({ type = 'default' }: TopnavItemsProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedMenu, setSelectedMenu] = useState<null | MenuItem>(null);
  const { pathname } = useLocation();
  const { isNestedItemOpen } = useNavContext();
  const { sessionUser } = useAuth();

  const activeSitemap = useMemo(() => {
    if (!sessionUser) return sitemap;

    const eventId = sessionUser.eventId;

    if (sessionUser.role === 'couple') return getCoupleSitemap(eventId, sessionUser.plan ?? 'free');
    if (sessionUser.role === 'uploader') {
      return getGuestSitemap(eventId, sessionUser.plan ?? 'free');
    }

    return sitemap;
  }, [sessionUser]);

  useEffect(() => {
    setAnchorEl(null);
    setSelectedMenu(null);
  }, [pathname]);

  return (
    <Stack
      sx={{
        alignItems: 'center',
        gap: '2px',
      }}
      className="nav-items"
    >
      {activeSitemap.map((menu: MenuItem) => (
        <Button
          key={menu.id}
          variant="text"
          className={clsx({
            active: isNestedItemOpen(menu.items),
          })}
          color={isNestedItemOpen(menu.items) ? 'primary' : 'neutral'}
          size={type === 'slim' ? 'small' : 'large'}
          endIcon={<IconifyIcon icon="material-symbols:expand-more-rounded" />}
          onClick={(event) => {
            setAnchorEl(event.currentTarget);
            setSelectedMenu(menu);
          }}
          sx={{ px: 2, fontSize: 14 }}
        >
          {menu.subheader}
        </Button>
      ))}
      {selectedMenu && (
        <NavitemPopover
          handleClose={() => {
            setAnchorEl(null);
            setSelectedMenu(null);
          }}
          anchorEl={anchorEl}
          open={!!anchorEl && !!selectedMenu}
          items={selectedMenu.items}
          level={0}
        />
      )}
    </Stack>
  );
};

export default TopnavItems;
